const DEMO_PATH_PREFIX = '/demo';
const DEMO_STORAGE_PREFIX = 'clocktower.demo.';
const STORAGE_SHIM_FLAG = '__clocktowerDemoStorageNamespaceInstalled';
const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

const isLocalHostname = (hostname) => LOCALHOST_HOSTNAMES.has(String(hostname || '').trim().toLowerCase());

export const isDemoMode = () => {
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }

  const pathname = String(window.location.pathname || '').toLowerCase();
  return pathname === DEMO_PATH_PREFIX || pathname.startsWith(`${DEMO_PATH_PREFIX}/`);
};

export const getAppBasename = () => (isDemoMode() ? DEMO_PATH_PREFIX : undefined);

export const getAppPath = (path = '/') => {
  const basename = getAppBasename() || '';
  const normalizedPath = String(path || '/').startsWith('/') ? String(path || '/') : `/${path}`;
  return `${basename}${normalizedPath}` || '/';
};

export const getStorageKey = (key) => `${isDemoMode() ? DEMO_STORAGE_PREFIX : ''}${key}`;

const shouldNamespaceStorageKey = (key) => {
  const normalizedKey = String(key || '');
  return isDemoMode() && normalizedKey && !normalizedKey.startsWith(DEMO_STORAGE_PREFIX);
};

export const installDemoStorageNamespace = () => {
  if (!isDemoMode() || typeof window === 'undefined' || !window.localStorage || !window.sessionStorage) {
    return;
  }

  const storagePrototype = Object.getPrototypeOf(window.localStorage);
  if (!storagePrototype || storagePrototype[STORAGE_SHIM_FLAG]) {
    return;
  }

  const originalGetItem = storagePrototype.getItem;
  const originalSetItem = storagePrototype.setItem;
  const originalRemoveItem = storagePrototype.removeItem;

  Object.defineProperty(storagePrototype, STORAGE_SHIM_FLAG, {
    value: true,
    configurable: false,
    enumerable: false
  });

  storagePrototype.getItem = function getItem(key) {
    const scopedKey = shouldNamespaceStorageKey(key) ? `${DEMO_STORAGE_PREFIX}${key}` : key;
    return originalGetItem.call(this, scopedKey);
  };

  storagePrototype.setItem = function setItem(key, value) {
    const scopedKey = shouldNamespaceStorageKey(key) ? `${DEMO_STORAGE_PREFIX}${key}` : key;
    return originalSetItem.call(this, scopedKey, value);
  };

  storagePrototype.removeItem = function removeItem(key) {
    const scopedKey = shouldNamespaceStorageKey(key) ? `${DEMO_STORAGE_PREFIX}${key}` : key;
    return originalRemoveItem.call(this, scopedKey);
  };
};

export const getStorageItem = (key) => {
  try {
    return typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem(getStorageKey(key))
      : null;
  } catch (error) {
    return null;
  }
};

export const setStorageItem = (key, value) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(getStorageKey(key), value);
    }
  } catch (error) {
    // Ignore storage write failures.
  }
};

export const removeStorageItem = (key) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(getStorageKey(key));
    }
  } catch (error) {
    // Ignore storage remove failures.
  }
};

export const removeStorageItems = (keys = []) => {
  keys.forEach(removeStorageItem);
};

export const getDemoApiBaseUrl = () => {
  const configuredDemoApiUrl = String(process.env.REACT_APP_DEMO_API_URL || '').trim();
  if (configuredDemoApiUrl) {
    return configuredDemoApiUrl;
  }

  if (typeof window !== 'undefined' && window.location) {
    const { protocol, hostname, port, origin } = window.location;
    if (isLocalHostname(hostname)) {
      const demoBackendPort = String(process.env.REACT_APP_DEMO_BACKEND_PORT || '3015');
      return String(port || '') === demoBackendPort
        ? `${origin}/demo-api`
        : `${protocol}//${hostname}:${demoBackendPort}/demo-api`;
    }

    return `${window.location.origin}/demo-api`;
  }

  return '/demo-api';
};